import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Box,
    Chip,
    Button,
    Tooltip,
    IconButton
} from '@mui/material';
import {
    Download as DownloadIcon,
    Share as ShareIcon,
    Verified as VerifiedIcon,
    Warning as WarningIcon,
    School as CourseIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useTranslate, useNotify } from 'react-admin';
import { Certificate, certificateService } from '../../services/certificateService';

interface CertificateCardProps {
    certificate: Certificate;
}

const CertificateCard: React.FC<CertificateCardProps> = ({ certificate }) => {
    const translate = useTranslate();
    const notify = useNotify();

    const handleDownload = () => {
        certificateService.downloadCertificate(certificate.uuid);
    };

    const handleShare = async () => {
        const success = await certificateService.copyVerifyUrl(certificate.verificationCode);
        if (success) {
            notify(translate('certificates.linkCopied'), { type: 'success' });
        } else {
            notify(translate('certificates.copyFailed'), { type: 'error' });
        }
    };

    const isExpiringSoon = certificate.expiresAt
        ? certificateService.isExpiringSoon(certificate.expiresAt, 30)
        : false;

    const daysUntilExpiry = certificate.expiresAt
        ? certificateService.getDaysUntilExpiry(certificate.expiresAt)
        : null;

    const getStatusChip = () => {
        if (certificate.isExpired) {
            return (
                <Chip
                    icon={<WarningIcon />}
                    label={translate('certificates.status.expired')}
                    color="error"
                    size="small"
                />
            );
        }
        if (isExpiringSoon && daysUntilExpiry !== null) {
            return (
                <Chip
                    icon={<WarningIcon />}
                    label={translate('certificates.status.expiringSoon', { days: daysUntilExpiry })}
                    color="warning"
                    size="small"
                />
            );
        }
        return (
            <Chip
                icon={<VerifiedIcon />}
                label={translate('certificates.status.valid')}
                color="success"
                size="small"
            />
        );
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: certificate.isExpired ? 0.7 : 1,
                borderLeft: 4,
                borderColor: certificate.isExpired
                    ? 'error.main'
                    : isExpiringSoon
                    ? 'warning.main'
                    : 'success.main'
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <CourseIcon color="primary" />
                    {getStatusChip()}
                </Box>

                <Typography variant="h6" gutterBottom>
                    {certificate.courseName}
                </Typography>

                {certificate.courseDescription && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {certificate.courseDescription.substring(0, 100)}
                        {certificate.courseDescription.length > 100 && '...'}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {translate('certificates.issuedAt')}: {certificateService.formatDate(certificate.issuedAt)}
                        </Typography>
                    </Box>

                    {certificate.expiresAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon fontSize="small" color={certificate.isExpired ? 'error' : 'action'} />
                            <Typography
                                variant="body2"
                                color={certificate.isExpired ? 'error.main' : 'text.secondary'}
                            >
                                {translate('certificates.expiresAt')}: {certificateService.formatDate(certificate.expiresAt)}
                            </Typography>
                        </Box>
                    )}

                    {certificate.score && (
                        <Typography variant="body2" color="text.secondary">
                            {translate('certificates.score')}: {certificate.score}%
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {translate('certificates.verificationCode')}: {certificate.verificationCode}
                    </Typography>
                </Box>
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ flexGrow: 1 }}
                >
                    {translate('certificates.download')}
                </Button>
                <Tooltip title={translate('certificates.shareTooltip')}>
                    <IconButton onClick={handleShare} color="primary">
                        <ShareIcon />
                    </IconButton>
                </Tooltip>
            </CardActions>
        </Card>
    );
};

export default CertificateCard;
